import { Component, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { i18nConfig, CTA } from '../../../../../interfaces';
import { timePassed } from '../../../../../util';

@Component({
  tag: 'zeudy-cta-overlay-controller',
  styleUrl: 'zeudy-cta-overlay-controller.css',
  shadow: false,
})
export class zeudyCtaOverlayController {
  @Prop() ended: boolean;
  @Prop({ reflect: true }) currentTime: number;
  @Prop({ reflect: true }) duration: number;
  @Prop() direction?: 'rtl';
  @Prop() cta?: CTA;
  @Prop() i18n: i18nConfig;
  @Prop() provider: string;

  @State() enabled: boolean;
  @State() show: boolean;
  @State() loading: boolean;
  @State() error: string;
  @State() skipped: boolean;
  @State() percentagePassed: number = 0;

  @Event() playVideo: EventEmitter<void>;
  @Event() pauseVideo: EventEmitter<boolean>;
  @Event() restartVideo: EventEmitter<void>;
  @Event() ctaStateChange: EventEmitter<boolean>;

  componentWillLoad() {
    this.handleDuration();
  }

  /**
   * Handle with the player is ended
   * @param val
   * @returns
   */
  @Watch('ended')
  handleEnded(val) {
    if (val) {
      if (this?.cta?.percentage !== 100) return;
      this.show = true;
    }
  }

  /**
   * Wait for duration to start before checking time
   * @returns void
   */
  @Watch('duration')
  handleDuration() {
    this.enabled = this.skipped ? false : this?.cta?.enabled;
    this.handleTime();
  }

  @Watch('currentTime')
  @Watch('duration')
  handlePercentagePassed() {
    this.percentagePassed = (this.currentTime / this.duration) * 100;
  }

  /**
   * Watch current time and check if we should
   * pause the video.
   */
  @Watch('currentTime')
  handleEnabled() {
    if (!this.show) return; // not showing.
    if (this.skipped) return; // already skipped.
    this.pauseVideo.emit(true); // pause if enabled.
  }

  /**
   * When current time changes, check to see if we should
   * enable the overlay
   * @returns void
   */
  @Watch('currentTime')
  handleTime() {
    if (!this.enabled) return; // not enabled.
    if (this.skipped) return; // skipped.
    if (this?.cta?.percentage === 100) return; // we'll catch this on ended event.
    this.checkTime();
  }

  /**
   * Set enabled/disabled based on time that has passed
   */
  checkTime() {
    this.show = timePassed({
      current: this.currentTime,
      duration: this.duration,
      showAfter: this?.cta?.percentage || 0,
    });
  }

  /**
   * Skip email collection
   */
  skip() {
    this.skipped = true;
    this.show = false;
    this.playVideo.emit();
  }

  /**
   * Handle rewatch click.
   */
  rewatch() {
    this.ended = false;
    this.show = false;
    this.restartVideo.emit();
  }

  /**
   * Maybe render
   * @returns JSX
   */

  @Watch('show')
  handleCtaStateChange(val) {
    this.ctaStateChange.emit(val);
  }
  render() {
    if (!this.show) {
      return;
    }

    return (
      <zeudy-cta-overlay-ui
        style={{
          '--zeudy-player-button-border-radius': `${this?.cta?.button_radius}px`,
          ...(this?.cta?.background_opacity ? { '--zeudy-player-cta-background-opacity': `${this?.cta?.background_opacity / 100}` } : {}),
          ...(this?.cta?.button_color
            ? {
                '--zeudy-player-button-color': `${this?.cta?.button_color}`,
              }
            : {}),
          ...(this?.cta?.button_text_color
            ? {
                '--zeudy-player-button-text': `${this?.cta?.button_text_color}`,
              }
            : {}),
        }}
        direction={this.direction}
        class="cta-overlay"
        i18n={this.i18n}
        headline={this?.cta?.headline}
        bottom-text={this?.cta?.bottom_text}
        show-button={this?.cta?.show_button}
        buttonLink={this?.cta?.button_link}
        allowSkip={!this.ended && this?.cta?.show_skip}
        allowRewatch={this.ended && this?.cta?.show_rewatch}
        button-text={this?.cta?.button_text}
        onSkip={() => this.skip()}
        onRewatch={() => this.rewatch()}
        provider={this.provider}
      ></zeudy-cta-overlay-ui>
    );
  }
}
